// src/utils/globalAudio.js
class GlobalAudio {
  constructor() {
    if (GlobalAudio.instance) {
      return GlobalAudio.instance;
    }
    
    this.audio = new Audio();
    this.listeners = new Map();
    this.currentPodcast = null;
    this.isPlaying = false;
    this.currentTime = 0;
    this.duration = 0;
    this.volume = 70;
    this.isLoading = false;
    this.shouldRestore = false;
    this.lastRestoreTime = 0;
    
    // Setup event listeners
    this.setupEventListeners();
    
    // Load state from localStorage on initialization
    this.loadStateFromStorage();
    
    GlobalAudio.instance = this;
  }

  setupEventListeners() {
    this.audio.addEventListener('timeupdate', () => {
      this.currentTime = this.audio.currentTime;
      this.duration = this.audio.duration;
      
      // Auto-save state every 5 seconds
      if (Date.now() - this.lastRestoreTime > 5000) {
        this.saveStateToStorage();
      }
      
      this.emit('timeupdate', {
        currentTime: this.currentTime,
        duration: this.duration,
        progress: this.duration ? (this.currentTime / this.duration) * 100 : 0
      });
    });

    this.audio.addEventListener('ended', () => {
      this.isPlaying = false;
      this.emit('ended');
      this.clearStateFromStorage();
    });

    this.audio.addEventListener('loadedmetadata', () => {
      this.duration = this.audio.duration;
      this.isLoading = false;
      
      // Restore playback position if needed
      if (this.shouldRestore && this.currentPodcast) {
        const savedState = this.loadStateFromStorage();
        if (savedState && savedState.currentPodcast?.id === this.currentPodcast.id) {
          const timeDiff = Date.now() - (savedState.timestamp || 0);
          // Only restore if within 1 hour and not too far off
          if (timeDiff < 3600000 && savedState.currentTime) {
            const restoreTime = Math.max(0, savedState.currentTime - 2); // Buffer 2 seconds
            this.audio.currentTime = restoreTime;
            this.currentTime = restoreTime;
            console.log('Restored playback position to:', restoreTime);
          }
        }
        this.shouldRestore = false;
      }
      
      this.emit('loadedmetadata', { duration: this.duration });
    });

    this.audio.addEventListener('canplay', () => {
      this.isLoading = false;
      this.emit('canplay');
    });

    this.audio.addEventListener('waiting', () => {
      this.isLoading = true;
      this.emit('waiting');
    });

    this.audio.addEventListener('playing', () => {
      this.isPlaying = true;
      this.isLoading = false;
      this.emit('playing');
    });

    this.audio.addEventListener('error', (e) => {
      this.isLoading = false;
      this.isPlaying = false;
      console.error('Audio error:', e);
      this.emit('error', { error: e });
    });
  }

  load(podcast, startTime = 0) {
    if (!podcast || !podcast.audioUrl) {
      console.error('No podcast or audio URL provided');
      return;
    }

    console.log('Loading podcast:', podcast.title, 'Start time:', startTime);
    
    // Check if we're already playing this podcast
    if (this.currentPodcast?.id === podcast.id && this.audio.src === podcast.audioUrl) {
      console.log('Same podcast already loaded');
      
      // If we're restoring, don't seek unless needed
      if (startTime > 0 && Math.abs(this.audio.currentTime - startTime) > 5) {
        this.seek(startTime);
      }
      return;
    }

    this.currentPodcast = podcast;
    this.isLoading = true;
    this.shouldRestore = true;
    
    // Save current state before loading new one
    this.saveStateToStorage();
    
    // Load new source
    this.audio.src = podcast.audioUrl;
    this.audio.currentTime = startTime;
    this.audio.load();
    
    this.emit('loading', { podcast });
  }

  play() {
    if (!this.audio.src) {
      console.error('No audio source to play');
      return Promise.reject(new Error('No audio source'));
    }
    
    // If paused and we have current time, continue from there
    if (this.audio.paused && this.currentTime > 0) {
      this.audio.currentTime = this.currentTime;
    }
    
    return this.audio.play().then(() => {
      this.isPlaying = true;
      this.saveStateToStorage();
    }).catch(error => {
      console.error('Error playing audio:', error);
      this.isPlaying = false;
      this.emit('error', { error });
    });
  }

  pause() {
    this.audio.pause();
    this.isPlaying = false;
    this.saveStateToStorage();
  }

  // NEW: Complete stop function - stops and resets audio
  stop() {
    console.log('Stopping audio completely');
    
    // Pause audio
    this.audio.pause();
    this.isPlaying = false;
    
    // Reset audio position
    this.audio.currentTime = 0;
    this.currentTime = 0;
    
    // Clear current podcast
    this.currentPodcast = null;
    
    // Clear storage
    this.clearStateFromStorage();
    
    // Emit stop event
    this.emit('stopped');
    
    console.log('Audio stopped and reset');
  }

  // NEW: Quick stop (just pause at current position)
  quickStop() {
    console.log('Quick stopping audio (pausing)');
    this.audio.pause();
    this.isPlaying = false;
    this.saveStateToStorage();
    this.emit('paused');
  }

  seek(time) {
    if (this.audio && !isNaN(time)) {
      this.audio.currentTime = time;
      this.currentTime = time;
      this.saveStateToStorage();
      console.log('Seek to:', time);
    }
  }

  skipForward() {
    if (this.audio && this.duration) {
      const newTime = this.currentTime + 15;
      this.seek(Math.min(this.duration, newTime));
    }
  }

  skipBackward() {
    if (this.audio) {
      const newTime = this.currentTime - 15;
      this.seek(Math.max(0, newTime));
    }
  }

  setVolume(volume) {
    const newVolume = Math.max(0, Math.min(100, volume));
    this.volume = newVolume;
    if (this.audio) {
      this.audio.volume = newVolume / 100;
    }
    this.saveStateToStorage();
  }

  on(event, callback) {
    if (!this.listeners.has(event)) {
      this.listeners.set(event, []);
    }
    this.listeners.get(event).push(callback);
  }

  off(event, callback) {
    if (this.listeners.has(event)) {
      const callbacks = this.listeners.get(event);
      const index = callbacks.indexOf(callback);
      if (index > -1) {
        callbacks.splice(index, 1);
      }
    }
  }

  emit(event, data = {}) {
    if (this.listeners.has(event)) {
      this.listeners.get(event).forEach(callback => {
        callback({ ...data, podcast: this.currentPodcast });
      });
    }
  }

  getState() {
    return {
      isPlaying: this.isPlaying,
      currentTime: this.currentTime,
      duration: this.duration,
      progress: this.duration ? (this.currentTime / this.duration) * 100 : 0,
      volume: this.volume,
      isLoading: this.isLoading,
      currentPodcast: this.currentPodcast
    };
  }

  saveStateToStorage() {
    const state = this.getState();
    if (state.currentPodcast) {
      localStorage.setItem('audioPlayerState', JSON.stringify({
        ...state,
        timestamp: Date.now()
      }));
    }
  }

  loadStateFromStorage() {
    try {
      const savedState = localStorage.getItem('audioPlayerState');
      if (savedState) {
        const state = JSON.parse(savedState);
        this.lastRestoreTime = Date.now();
        return state;
      }
    } catch (e) {
      console.error('Error loading audio state:', e);
    }
    return null;
  }

  clearStateFromStorage() {
    localStorage.removeItem('audioPlayerState');
  }
}

// Create singleton instance
const globalAudio = new GlobalAudio();

// Initialize and restore on page load
window.addEventListener('load', () => {
  const savedState = globalAudio.loadStateFromStorage();
  if (savedState && savedState.currentPodcast) {
    console.log('Found saved audio state for:', savedState.currentPodcast.title);
  }
});

export { globalAudio };